import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {Briefcase, Users, FileText, Plus, Edit3, Trash2, Eye, X, AlertCircle, LogOut, RefreshCw, Download, Mail, FolderOpen, Clock, ShieldCheck, ShieldX, BadgeCheck, BadgeX, Building2, Bell, UserCircle, MessageSquare, Image as ImageIcon} from 'lucide-react';
import AccountSettings from '@/components/AccountSettings';
import JobShareImageModal from '@/components/JobShareImageModal';
import MobileBottomNav from '@/components/MobileBottomNav';
import JobFormModal from '@/components/JobFormModal';
import toast from "react-hot-toast";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import DashboardThemeWrapper from "@/components/DashboardThemeWrapper";
import { CONTRACT_LABELS, ContractType } from "@/data/jobs";
import { useAuth } from "@/hooks/useAuth";
import { useRecruiterJobs, RecruiterJob, NewRecruiterJob } from "@/hooks/useRecruiterJobs";
import { lumi } from "@/lib/lumi";
import { useApplications, Application, APPLICATION_STATUS_LABELS, ApplicationStatus } from "@/hooks/useApplications";
import { fetchProDossierByCreator } from "@/hooks/useProDossier";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import CompanyProfileForm from "@/components/CompanyProfileForm";
import BulkJobImport from "@/components/BulkJobImport";
import CandidateAlertsManager from "@/components/CandidateAlertsManager";
import { checkAndNotifyCandidateAlerts } from "@/lib/candidateAlertsService";
import type { NewCandidateAlert } from "@/hooks/useCandidateAlerts";
import { useMessaging } from "@/hooks/useMessaging";

// Clé sessionStorage du brouillon d'annonce initié depuis le hero de /offres
// (dépôt d'annonce avant création/connexion du compte recruteur).
const PENDING_JOB_DRAFT_KEY = "bnssa-mns:pending-job-draft";

// ─── Badge statut dossier AE ─────────────────────────────────────────────────

type DossierStatus = 'pending' | 'approved' | 'rejected' | null

function DossierStatusBadge({ status }: { status: DossierStatus }) {
  if (!status) return null
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
        <ShieldCheck className="w-3 h-3" />
        Dossier validé
      </span>
    )
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
        <ShieldX className="w-3 h-3" />
        Dossier refusé
      </span>
    )
  }
  // pending
  return (
    <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
      <Clock className="w-3 h-3" />
      Dossier en attente
    </span>
  )
}

// ─── Badge statut validation offre (pending/approved/rejected) ───────────────

type JobValidationStatus = 'pending' | 'approved' | 'rejected'

function JobValidationBadge({ status, reason }: { status?: string | null; reason?: string | null }) {
  const normalized: JobValidationStatus =
    status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'pending'

  if (normalized === 'approved') {
    return (
      <span
        className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold px-2 py-0.5 rounded-full"
        title="Offre validée et publiée"
      >
        <BadgeCheck className="w-3.5 h-3.5" />
        Publiée
      </span>
    )
  }
  if (normalized === 'rejected') {
    return (
      <span
        className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-[11px] font-bold px-2 py-0.5 rounded-full"
        title={reason ? `Refusée — Motif : ${reason}` : 'Offre refusée par l\'administration'}
      >
        <BadgeX className="w-3.5 h-3.5" />
        Refusée
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold px-2 py-0.5 rounded-full"
      title="En attente de validation par l'administration"
    >
      <Clock className="w-3.5 h-3.5" />
      En attente
    </span>
  )
}

// ─── Modal détail candidature ────────────────────────────────────────────────

interface ApplicationDetailModalProps {
  application: Application;
  dossierStatus?: DossierStatus;
  onClose: () => void;
  onDeleteJob?: (jobId: string) => void;
  onAskQuestion?: (application: Application) => void;
  asking?: boolean;
}

function ApplicationDetailModal({ application, dossierStatus, onClose, onDeleteJob, onAskQuestion, asking }: ApplicationDetailModalProps) {
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-5 flex items-start sm:items-center justify-between gap-3 z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 break-words">Candidature de {application.fullName}</h2>
            <p className="text-xs text-gray-500 break-words">{application.jobTitle} — {application.email}</p>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="p-1.5 hover:bg-gray-100 rounded-md flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {onAskQuestion && (
          <div className="px-4 sm:px-6 pt-4">
            <button
              type="button"
              disabled={asking}
              onClick={() => onAskQuestion(application)}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
              title="Démarrer une conversation interne avec ce candidat"
            >
              <MessageSquare className="w-4 h-4" />
              {asking ? "Ouverture de la conversation…" : "Poser une question au candidat"}
            </button>
            <p className="text-[11px] text-gray-500 italic mt-1.5 text-center">
              La messagerie interne préserve les échanges sur Emplois BNSSA & MNS et notifie le candidat par email.
            </p>
          </div>
        )}

        <div className="p-6 space-y-5">
          {application.cvUrl && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-blue-900 min-w-0 flex-1">
                <FileText className="w-4 h-4 flex-shrink-0" />
                <span className="font-semibold truncate">CV : {application.cvFileName || "document"}</span>
              </div>
              <a
                href={application.cvUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md flex-shrink-0"
              >
                <Download className="w-3.5 h-3.5" />
                Télécharger
              </a>
            </div>
          )}

          {application.message ? (
            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Lettre de motivation
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed break-words">
                {application.message}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Aucune lettre de motivation fournie.</p>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5">
              <div className="text-gray-500">Téléphone</div>
              <div className="font-semibold text-gray-900">{application.phone || "—"}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5">
              <div className="text-gray-500">Tarif proposé</div>
              <div className="font-semibold text-gray-900">
                {typeof application.proposedRate === "number" ? `${application.proposedRate} €/h` : "—"}
              </div>
            </div>
          </div>

          {application.contractType === 'auto-entrepreneur' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3">
              <FolderOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-700 mb-1">Dossier professionnel Auto-Entrepreneur</p>
                <DossierStatusBadge status={dossierStatus ?? null} />
                {!dossierStatus && <span className="text-[11px] text-gray-400 italic">Aucun dossier soumis</span>}
              </div>
            </div>
          )}
          <p className="text-[11px] text-gray-400 italic">
            Le détail des pièces (carte pro, assurance, diplômes…) est réservé à l'administrateur de la plateforme.
          </p>

          {onDeleteJob && application.jobId && (
            <div className="pt-3 border-t border-gray-200">
              <p className="text-[11px] text-gray-500 mb-2">
                Vous pouvez supprimer définitivement l'annonce liée à cette candidature.
              </p>
              <button
                type="button"
                onClick={() => onDeleteJob(application.jobId)}
                className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 text-xs font-bold px-3 py-2 rounded-md"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Supprimer l'annonce « {application.jobTitle} »
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page principale Backoffice ───────────────────────────────────────────────

export default function Backoffice() {
  const navigate = useNavigate();
  const { user, profile, isAuthenticated, isCandidate, signOut } = useAuth();

  const {
    jobs,
    loading: jobsLoading,
    fetchMyJobs,
    createJob,
    updateJob,
    deleteJob,
  } = useRecruiterJobs();

  const {
    applications,
    loading: appsLoading,
    setApplications,
    updateStatus,
  } = useApplications();

  // Messagerie interne — pour permettre au recruteur de poser une question au candidat
  const { findOrCreateConversation, conversations: messagingConversations } = useMessaging(user?.userId);
  const [askingAppId, setAskingAppId] = useState<string | null>(null);

  // Compteur de messages non lus pour le bouton « Messagerie » du hero
  const unreadMessagesCount = useMemo(() => {
    if (!user?.userId || !Array.isArray(messagingConversations)) return 0;
    return messagingConversations.reduce(
      (sum, c: { unreadCounts?: Record<string, number> }) =>
        sum + (c?.unreadCounts?.[user.userId] || 0),
      0
    );
  }, [messagingConversations, user?.userId]);

  const [editing, setEditing] = useState<RecruiterJob | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState<"offres" | "candidats" | "alertes" | "coordonnees" | "compte">(() => {
    if (typeof window === "undefined") return "offres";
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t === "alertes" || t === "candidats" || t === "coordonnees" || t === "offres" || t === "compte") return t;
    return "offres";
  });

  // Brouillon d'alerte candidat (depuis /disponibles, stocké dans sessionStorage)
  const [candidateAlertDraft, setCandidateAlertDraft] = useState<Partial<NewCandidateAlert> | undefined>(undefined);

  // Profil entreprise du recruteur
  const { profile: companyProfile, isComplete: isCompanyComplete } = useCompanyProfile(
    user?.userId,
    user?.email,
    'recruiter'
  );

  // Candidature sélectionnée pour afficher la lettre de motivation en modal
  const [viewingApp, setViewingApp] = useState<Application | null>(null);

  // Offre sélectionnée pour générer un visuel de partage social (LinkedIn/Facebook)
  const [sharingJob, setSharingJob] = useState<RecruiterJob | null>(null);

  // Statuts dossiers AE — map candidateId → DossierStatus
  // PAS DE CACHE : on refetch systématiquement pour garantir la fraîcheur des statuts admin.
  const [dossierStatuses, setDossierStatuses] = useState<Record<string, DossierStatus>>({});

  // Route protection
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/inscription", { replace: true });
    } else if (isCandidate) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, isCandidate, navigate]);

  // Récupération d'un brouillon d'alerte candidat depuis sessionStorage (CTA /disponibles → recruteur non connecté)
  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem("candidate-alert-draft");
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<NewCandidateAlert>;
        if (parsed && typeof parsed === "object") {
          setCandidateAlertDraft(parsed);
          setTab("alertes");
          window.sessionStorage.removeItem("candidate-alert-draft");
        }
      }
    } catch (e) {
      console.warn("[Backoffice] candidate alert draft parse error", e);
    }
  }, [isAuthenticated]);

  // Déclenchement du service d'alertes candidats à la connexion (rate-limit interne au service)
  useEffect(() => {
    if (!user?.userId) return;
    void checkAndNotifyCandidateAlerts({
      userId: user.userId,
      userEmail: user.email,
    }).catch((e) => console.warn("[Backoffice] candidate alerts check failed", e));
  }, [user?.userId, user?.email]);

  // Initial data load — only for the connected recruiter
  const recruiterId = user?.userId;

  useEffect(() => {
    if (!recruiterId) return;
    fetchMyJobs(recruiterId);
  }, [recruiterId, fetchMyJobs]);

  // ─── Étape 2 du dépôt d'annonce depuis /offres ──────────────────────────────
  // Si un recruteur a initié le dépôt d'une annonce depuis le hero de /offres
  // AVANT d'avoir un compte, l'annonce est stockée en sessionStorage. Une fois
  // le compte créé / connecté et les coordonnées complétées, on crée l'offre
  // automatiquement, puis on bascule sur l'onglet « Mes offres ».
  // Tant que les coordonnées ne sont pas complètes, on garde le brouillon et on
  // invite l'utilisateur à compléter ses coordonnées (le brouillon n'est PAS perdu).
  useEffect(() => {
    if (!recruiterId) return;
    if (typeof window === "undefined") return;
    let raw: string | null = null;
    try {
      raw = window.sessionStorage.getItem(PENDING_JOB_DRAFT_KEY);
    } catch {
      raw = null;
    }
    if (!raw) return;

    // Coordonnées incomplètes → on conserve le brouillon, on guide l'utilisateur.
    if (!isCompanyComplete) {
      toast(
        "Votre annonce est prête ! Complétez d'abord vos coordonnées pour la publier.",
        { icon: "📝", duration: 6000 }
      );
      setTab("coordonnees");
      return;
    }

    let draft: NewRecruiterJob | null = null;
    try {
      draft = JSON.parse(raw) as NewRecruiterJob;
    } catch (e) {
      console.warn("[Backoffice] pending job draft parse error", e);
      try { window.sessionStorage.removeItem(PENDING_JOB_DRAFT_KEY); } catch { /* ignore */ }
      return;
    }
    if (!draft || typeof draft !== "object" || !draft.title) {
      try { window.sessionStorage.removeItem(PENDING_JOB_DRAFT_KEY); } catch { /* ignore */ }
      return;
    }

    // On consomme immédiatement le brouillon pour éviter toute double création.
    try { window.sessionStorage.removeItem(PENDING_JOB_DRAFT_KEY); } catch { /* ignore */ }

    (async () => {
      try {
        await createJob(draft as NewRecruiterJob, recruiterId, user?.email, (user as { userName?: string })?.userName);
        await fetchMyJobs(recruiterId);
        setTab("offres");
        toast.success("Votre annonce a été déposée ! Elle sera publiée après validation.");
      } catch (e) {
        console.error("[Backoffice] pending job draft creation failed", e);
        toast.error("Impossible de finaliser le dépôt de l'annonce. Réessayez via « Nouvelle offre ».");
      }
    })();
  }, [recruiterId, isCompanyComplete, createJob, fetchMyJobs, user]);

  // Load applications targeting this recruiter's offers.
  // Important : la requête `applications.list` avec un filtre `$in` (multi-jobs) renvoie
  // parfois une réponse vide à cause de l'encodage base64 GET. On contourne en chargeant
  // chaque jobId individuellement en parallèle, puis on fusionne et on trie.
  const myJobIds = useMemo(() => jobs.map((j) => j._id), [jobs]);
  const myJobIdsKey = useMemo(() => myJobIds.join('|'), [myJobIds]);

  useEffect(() => {
    if (!myJobIds.length) {
      setApplications([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          myJobIds.map(async (jid) => {
            try {
              const { list } = await lumi.entities.applications.list({
                filter: { jobId: jid },
                sort: { createdAt: -1 },
              });
              // Normaliser _id en string pure pour garantir les comparaisons ===
              return (list as Application[]).map((a) => ({
                ...a,
                _id: String((a._id as unknown as { $oid?: string })?.$oid ?? a._id),
              }));
            } catch (e) {
              console.error('applications.list failed for job', jid, e);
              return [] as Application[];
            }
          })
        );
        if (cancelled) return;
        const flat = results.flat();
        // Déduplique par _id et trie par createdAt desc
        const byId = new Map<string, Application>();
        flat.forEach((a) => byId.set(a._id, a));
        const merged = Array.from(byId.values()).sort((a, b) =>
          (b.createdAt || '').localeCompare(a.createdAt || '')
        );
        setApplications(merged);
      } catch (e) {
        console.error('applications batch fetch failed:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [myJobIdsKey, myJobIds, setApplications]);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Vous êtes déconnecté.");
    navigate("/", { replace: true });
  };

  const handleDelete = async (id: string) => {
    // Compter les candidatures liées pour avertir le recruteur de la perte associée
    const linkedCount = applications.filter((a) => a.jobId === id).length;
    const job = jobs.find((j) => j._id === id);
    const title = job?.title ? `« ${job.title} »` : "cette annonce";
    const warning =
      linkedCount > 0
        ? `Supprimer définitivement ${title} ?\n\n⚠️ ${linkedCount} candidature${linkedCount > 1 ? "s" : ""} liée${linkedCount > 1 ? "s" : ""} ne sera${linkedCount > 1 ? "ont" : ""} plus rattachée${linkedCount > 1 ? "s" : ""} à une offre active.`
        : `Supprimer définitivement ${title} ?`;
    if (!confirm(warning)) return;
    try {
      await deleteJob(id);
      toast.success("Offre supprimée");
      // Si la modale de candidature affichait cette offre, la fermer
      setViewingApp((prev) => (prev && prev.jobId === id ? null : prev));
      if (recruiterId) await fetchMyJobs(recruiterId);
    } catch (err) {
      console.error(err);
      toast.error("Suppression impossible.");
    }
  };

  const handleSave = async (data: NewRecruiterJob, id?: string) => {
    if (!recruiterId) {
      toast.error("Session expirée, veuillez vous reconnecter.");
      return;
    }
    try {
      if (id) {
        await updateJob(id, data);
        toast.success("Offre mise à jour");
      } else {
        await createJob(data, recruiterId, user?.email, (user as { userName?: string })?.userName);
        toast.success("Offre créée");
      }
      await fetchMyJobs(recruiterId);
      setEditing(null);
      setShowForm(false);
    } catch (err) {
      console.error(err);
      toast.error("Échec de l'enregistrement.");
    }
  };

  // Démarre (ou rouvre) une conversation entre le recruteur et le candidat à propos de cette candidature
  const handleAskQuestion = async (app: Application) => {
    if (!user?.userId) {
      toast.error("Session expirée, veuillez vous reconnecter.");
      return;
    }
    if (!app.creator) {
      toast.error("Identifiant candidat introuvable pour cette candidature.");
      return;
    }
    if (app.creator === user.userId) {
      toast.error("Vous ne pouvez pas vous écrire à vous-même.");
      return;
    }
    setAskingAppId(app._id);
    try {
      const conv = await findOrCreateConversation({
        peerUserId: app.creator,
        peerRole: 'candidate',
        peerName: app.fullName,
        peerEmail: app.email,
        currentRole: 'recruiter',
        currentName: companyProfile?.raisonSociale || profile?.fullName || user?.userName || user?.email,
        currentEmail: user?.email,
        context: {
          type: 'application',
          applicationId: app._id,
          jobId: app.jobId,
          jobTitle: app.jobTitle,
        },
        subject: `Candidature — ${app.jobTitle}`,
      });
      if (conv?._id) {
        toast.success("Conversation ouverte avec le candidat.");
        navigate(`/messages?c=${conv._id}`);
      } else {
        toast.error("Impossible d'ouvrir la conversation.");
      }
    } catch (e) {
      console.error('[Backoffice] handleAskQuestion failed:', e);
      toast.error("Échec de l'ouverture de la conversation.");
    } finally {
      setAskingAppId(null);
    }
  };

  const handleStatusChange = async (id: string, status: ApplicationStatus) => {
    try {
      // Normaliser l'id en string pure (protection contre ObjectId sérialisé)
      const normalizedId = String((id as unknown as { $oid?: string })?.$oid ?? id);
      // Chercher le doc en mémoire (les _id ont été normalisés au fetch)
      const knownApp = applications.find(
        (a) => String((a._id as unknown as { $oid?: string })?.$oid ?? a._id) === normalizedId
      );
      console.log('[backoffice] handleStatusChange', { id, normalizedId, found: !!knownApp, email: knownApp?.email });
      const result = await updateStatus(normalizedId, status, knownApp);
      if (result?.emailSent) {
        toast.success(`Statut mis à jour — email envoyé au candidat ✓`);
      } else if (result && !result.emailSent) {
        toast.success(`Statut mis à jour`);
        toast(`⚠️ Email candidat non envoyé : ${result.emailReason || 'raison inconnue'}`, { duration: 6000, icon: '✉️' });
      } else {
        toast.success("Statut mis à jour");
      }
      // Mise à jour optimiste de la liste locale
      setApplications((prev) => prev.map((a) => (a._id === normalizedId ? { ...a, status } : a)));
    } catch (err) {
      console.error(err);
      toast.error("Mise à jour impossible.");
    }
  };

  // Fonction de chargement des statuts AE — TOUJOURS depuis pro_dossiers, AUCUN CACHE.
  // Chaque appel refait les requêtes pour garantir la fraîcheur des statuts admin.
  const loadDossierStatuses = useCallback(async (apps: Application[]) => {
    const aeApps = apps.filter((a) => a.contractType === 'auto-entrepreneur' && a.creator);
    if (!aeApps.length) return;

    // Dédupliquer par creator (un même candidat peut postuler à plusieurs offres)
    const uniqueCreators = new Map<string, { creator: string; email?: string }>();
    aeApps.forEach((a) => {
      if (!uniqueCreators.has(a.creator)) {
        uniqueCreators.set(a.creator, { creator: a.creator, email: a.email });
      }
    });

    const hasEmbeddedDossierByCreator = new Map<string, boolean>();
    aeApps.forEach((a) => {
      const has = Boolean(
        (a as unknown as { dossier?: { files?: Record<string, unknown> } })?.dossier?.files &&
          Object.keys(
            (a as unknown as { dossier: { files: Record<string, unknown> } }).dossier.files
          ).length > 0
      );
      if (has) hasEmbeddedDossierByCreator.set(a.creator, true);
    });

    const results = await Promise.all(
      Array.from(uniqueCreators.values()).map(async ({ creator, email }) => {
        try {
          const dossier = await fetchProDossierByCreator(creator, email);
          const officialStatus =
            ((dossier as unknown as Record<string, unknown>)?.validationStatus as DossierStatus) ?? null;
          if (officialStatus) {
            return { creator, status: officialStatus };
          }
          if (hasEmbeddedDossierByCreator.get(creator)) {
            return { creator, status: 'pending' as DossierStatus };
          }
          return { creator, status: null as DossierStatus };
        } catch {
          return {
            creator,
            status: hasEmbeddedDossierByCreator.get(creator)
              ? ('pending' as DossierStatus)
              : (null as DossierStatus),
          };
        }
      })
    );

    // Remplacement complet (pas de merge) pour refléter aussi les transitions vers null
    const next: Record<string, DossierStatus> = {};
    results.forEach(({ creator, status }) => {
      next[creator] = status;
    });
    setDossierStatuses(next);
  }, []);

  // Chargement initial à chaque changement de liste d'applications
  useEffect(() => {
    if (applications.length > 0) {
      loadDossierStatuses(applications);
    } else {
      setDossierStatuses({});
    }
  }, [applications, loadDossierStatuses]);

  // POLLING AUTOMATIQUE toutes les 10s tant que l'onglet "Candidatures" est actif
  // et que la fenêtre est visible. Garantit que les changements de statut faits par
  // l'admin apparaissent en quasi temps réel côté recruteur.
  useEffect(() => {
    if (tab !== 'candidats') return;
    if (applications.length === 0) return;

    // Refresh immédiat à l'arrivée sur l'onglet
    loadDossierStatuses(applications);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadDossierStatuses(applications);
      }
    }, 10000);

    // Refresh aussi quand l'onglet redevient visible (retour de focus)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadDossierStatuses(applications);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [tab, applications, loadDossierStatuses]);

  const refreshAll = async () => {
    if (!recruiterId) return;
    setDossierStatuses({});
    await fetchMyJobs(recruiterId);
    // Recharger les candidatures par jobId individuellement
    let merged: Application[] = [];
    if (myJobIds.length) {
      try {
        const results = await Promise.all(
          myJobIds.map((jid) =>
            lumi.entities.applications
              .list({ filter: { jobId: jid }, sort: { createdAt: -1 } })
              .then((r) =>
                (r.list as Application[]).map((a) => ({
                  ...a,
                  _id: String((a._id as unknown as { $oid?: string })?.$oid ?? a._id),
                }))
              )
              .catch(() => [] as Application[])
          )
        );
        const flat = results.flat();
        const byId = new Map<string, Application>();
        flat.forEach((a) => byId.set(a._id, a));
        merged = Array.from(byId.values()).sort((a, b) =>
          (b.createdAt || '').localeCompare(a.createdAt || '')
        );
        setApplications(merged);
      } catch (e) {
        console.error(e);
      }
    }
    // Force-refresh immédiat des statuts dossiers AE depuis pro_dossiers
    if (merged.length) {
      await loadDossierStatuses(merged);
    }
    toast.success("Données rafraîchies");
  };

  return (
    <Layout>
      <Seo
        title="Espace Recruteur — Back-office Emplois BNSSA & MNS"
        description="Gérez vos offres d'emploi BNSSA et MNS, consultez les candidatures et trouvez les meilleurs profils."
      />

      <DashboardThemeWrapper>
      <section className="bg-gradient-to-br from-gray-900 to-slate-800 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 sm:flex-1">
              <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                <Briefcase className="w-3.5 h-3.5" />
                Espace Recruteur
              </span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold break-words">Back-office</h1>
              <p className="text-gray-300 mt-2 break-words">
                {profile?.company
                  ? `Bonjour ${profile?.fullName || user?.userName || user?.email} — ${profile.company}`
                  : `Bonjour ${profile?.fullName || user?.userName || user?.email || ""}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <button
                onClick={refreshAll}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Rafraîchir
              </button>
              <button
                onClick={() => {
                  if (!isCompanyComplete) {
                    toast.error("Veuillez d'abord compléter vos coordonnées dans l'onglet « Mes coordonnées ».");
                    setTab("coordonnees");
                    return;
                  }
                  setEditing(null);
                  setShowForm(true);
                }}
                className="bg-white text-gray-900 px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nouvelle offre
              </button>
              <Link
                to="/messages"
                className="relative inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                aria-label={unreadMessagesCount > 0 ? `Messagerie (${unreadMessagesCount} non lus)` : "Messagerie"}
              >
                <MessageSquare className="w-4 h-4" />
                Messagerie
                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 inline-flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full ring-2 ring-gray-900">
                    {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
                  </span>
                )}
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Offres actives", value: jobs.length, icon: Briefcase, color: "blue" },
            { label: "Candidatures", value: applications.length, icon: Users, color: "green" },
            { label: "Offres BNSSA", value: jobs.filter((j) => j.category === "BNSSA").length, icon: FileText, color: "red" },
            { label: "Offres MNS", value: jobs.filter((j) => j.category === "MNS").length, icon: FileText, color: "blue" },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  s.color === "blue" ? "bg-blue-100 text-blue-600" :
                  s.color === "red" ? "bg-red-100 text-red-600" :
                  "bg-green-100 text-green-600"
                }`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                  <div className="text-xs text-gray-600">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs (cachés en mobile, remplacés par la bottom nav) */}
      <section className="hidden md:block border-b border-gray-200 bg-white sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto no-scrollbar">
          {[
            { id: "offres" as const, label: "Mes offres", icon: Briefcase },
            { id: "candidats" as const, label: "Candidatures", icon: Users },
            { id: "alertes" as const, label: "Alertes candidats", icon: Bell },
            { id: "coordonnees" as const, label: "Mes coordonnées", icon: Building2 },
            { id: "compte" as const, label: "Mon compte", icon: UserCircle },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
                tab === t.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.id === "coordonnees" && !isCompanyComplete && (
                <span className="ml-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-amber-500" aria-label="Coordonnées à compléter" />
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Avertissement coordonnées manquantes */}
          {!isCompanyComplete && tab !== "coordonnees" && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-bold text-amber-900">Coordonnées de l'entreprise à compléter</p>
                <p className="text-amber-800 text-xs mt-0.5">
                  Avant de publier votre première offre, renseignez votre raison sociale, adresse, téléphone, nom du responsable et SIRET. Ces informations restent privées et ne sont pas visibles sur les annonces.
                </p>
              </div>
              <button
                onClick={() => setTab("coordonnees")}
                className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
              >
                Compléter
              </button>
            </div>
          )}

          {tab === "coordonnees" ? (
            <CompanyProfileForm
              creator={user?.userId || ""}
              email={user?.email}
              accountType="recruiter"
              theme="recruiter"
              intro="Ces informations sont obligatoires avant la publication de toute offre. Elles ne sont jamais affichées sur les annonces ni accessibles aux candidats — elles servent uniquement à l'administration de bnssa-mns.fr."
              onSaved={() => setTab("offres")}
            />
          ) : tab === "alertes" ? (
            recruiterId ? (
              <CandidateAlertsManager
                recruiterId={recruiterId}
                recruiterEmail={user?.email}
                initialDraft={candidateAlertDraft}
              />
            ) : (
              <div className="p-8 text-center text-sm text-gray-500">Chargement…</div>
            )
          ) : tab === "offres" ? (
            <>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {jobsLoading && (
                <div className="p-4 text-center text-sm text-gray-500">Chargement…</div>
              )}
              {!jobsLoading && jobs.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-500">
                  Aucune offre publiée pour le moment. Cliquez sur <strong>« Nouvelle offre »</strong> pour commencer.
                </div>
              )}
              {!jobsLoading && jobs.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Titre</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Statut</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Catégorie</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">Contrat</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Lieu</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {jobs.map((j) => {
                        const rj = j as RecruiterJob & { validationStatus?: string; rejectionReason?: string }
                        return (
                        <tr key={j._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900 truncate max-w-[160px] sm:max-w-[240px] md:max-w-xs">{j.title}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[160px] sm:max-w-[240px] md:max-w-xs">{j.employer}</div>
                          </td>
                          <td className="px-4 py-3">
                            <JobValidationBadge status={rj.validationStatus} reason={rj.rejectionReason} />
                            {rj.validationStatus === 'rejected' && rj.rejectionReason && (
                              <div className="text-[10px] text-red-600 mt-1 max-w-[200px] line-clamp-2" title={rj.rejectionReason}>
                                {rj.rejectionReason}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${
                              j.category === "BNSSA" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                            }`}>
                              {j.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell text-gray-700 text-xs">{CONTRACT_LABELS[j.contractType]}</td>
                          <td className="px-4 py-3 hidden md:table-cell text-gray-700 text-xs">{j.location}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/offre/${j._id}`}
                                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                                aria-label="Voir l'offre"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                              <button
                                onClick={() => setSharingJob(j)}
                                className="p-1.5 text-fuchsia-600 hover:bg-fuchsia-50 rounded"
                                aria-label="Générer un visuel de partage"
                                title="Générer un visuel de partage (LinkedIn/Facebook)"
                              >
                                <ImageIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditing(j);
                                  setShowForm(true);
                                }}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                aria-label="Éditer"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(j._id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                aria-label="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
              {recruiterId && isCompanyComplete && (
                <div className="mt-6">
                  <BulkJobImport
                    recruiterId={recruiterId}
                    recruiterEmail={user?.email}
                    recruiterName={(user as { userName?: string })?.userName}
                    onImported={() => fetchMyJobs(recruiterId)}
                  />
                </div>
              )}
            </>
          ) : tab === "candidats" ? (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 bg-blue-50 border-b border-blue-200 flex items-center gap-2 text-sm text-blue-800">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                Candidatures reçues sur vos offres publiées.
              </div>
              {appsLoading && (
                <div className="p-4 text-center text-sm text-gray-500">Chargement…</div>
              )}
              {!appsLoading && applications.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-500">
                  Aucune candidature pour le moment.
                </div>
              )}
              {!appsLoading && applications.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Candidat</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Offre</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden lg:table-cell">Tarif</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Documents</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Statut</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-700">Annonce</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {applications.map((c) => (
                        <tr key={c._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900 break-words max-w-[200px]">{c.fullName}</div>
                            <div className="text-xs text-gray-500 break-all max-w-[200px]">{c.email}</div>
                            {c.phone && <div className="text-xs text-gray-500 break-words">{c.phone}</div>}
                            {c.contractType === 'auto-entrepreneur' && (
                              <div className="mt-1">
                                <DossierStatusBadge status={dossierStatuses[c.creator] ?? null} />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-gray-700 text-xs">
                            <div className="font-medium break-words max-w-[240px]">{c.jobTitle}</div>
                            <div className="text-gray-500 break-words max-w-[240px]">{CONTRACT_LABELS[c.contractType as ContractType]} — {c.jobCategory}</div>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell text-gray-700 text-xs">
                            {typeof c.proposedRate === "number" ? (
                              <span className="font-bold">{c.proposedRate} €/h</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              {c.cvUrl ? (
                                <a
                                  href={c.cvUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                                  title={c.cvFileName || "Voir le CV"}
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  CV
                                </a>
                              ) : c.cvFileName ? (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500" title="CV non uploadable (ancien format)">
                                  <FileText className="w-3.5 h-3.5" />
                                  {c.cvFileName}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-300">Pas de CV</span>
                              )}
                              {c.message ? (
                                <button
                                  type="button"
                                  onClick={() => setViewingApp(c)}
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                  Lettre
                                </button>
                              ) : null}
                              <button
                                type="button"
                                disabled={askingAppId === c._id}
                                onClick={() => handleAskQuestion(c)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline disabled:opacity-60 disabled:cursor-wait"
                                title="Poser une question au candidat via la messagerie interne"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                {askingAppId === c._id ? "…" : "Message"}
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={c.status}
                              onChange={(e) => handleStatusChange(c._id, e.target.value as ApplicationStatus)}
                              className={`text-xs font-semibold rounded border border-gray-300 px-2 py-1 bg-white focus:ring-2 focus:ring-blue-500 outline-none ${
                                c.status === "nouveau" ? "text-blue-700" :
                                c.status === "en_examen" ? "text-amber-700" :
                                c.status === "entretien" ? "text-indigo-700" :
                                c.status === "retenu" ? "text-green-700" :
                                "text-red-700"
                              }`}
                            >
                              {(Object.keys(APPLICATION_STATUS_LABELS) as ApplicationStatus[]).map((s) => (
                                <option key={s} value={s}>{APPLICATION_STATUS_LABELS[s]}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {c.jobId ? (
                              <button
                                type="button"
                                onClick={() => handleDelete(c.jobId)}
                                className="inline-flex items-center gap-1 p-1.5 text-red-600 hover:bg-red-50 rounded"
                                aria-label={`Supprimer l'annonce ${c.jobTitle}`}
                                title="Supprimer l'annonce liée à cette candidature"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <AccountSettings email={user?.email} accent="blue" />
          )}
        </div>
      </section>

      {/* Modal Edit/Create */}
      {showForm && (
        <JobFormModal
          existing={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={(data) => handleSave(data, editing?._id)}
        />
      )}

      {/* Modal visuel de partage social */}
      {sharingJob && (
        <JobShareImageModal
          job={{
            title: sharingJob.title,
            employer: sharingJob.employer,
            location: sharingJob.location,
            department: sharingJob.department,
            category: sharingJob.category,
            contractType: sharingJob.contractType,
          }}
          onClose={() => setSharingJob(null)}
        />
      )}

      {/* Modal lettre de motivation */}
      {viewingApp && (
        <ApplicationDetailModal
          application={viewingApp}
          dossierStatus={viewingApp.contractType === 'auto-entrepreneur' ? (dossierStatuses[viewingApp.creator] ?? null) : undefined}
          onClose={() => setViewingApp(null)}
          onDeleteJob={handleDelete}
          onAskQuestion={handleAskQuestion}
          asking={askingAppId === viewingApp._id}
        />
      )}
      {/* Bottom navigation mobile-only */}
      <MobileBottomNav
        ariaLabel="Navigation recruteur"
        accent="blue"
        active={tab}
        onChange={(id) => setTab(id)}
        items={[
          { id: "offres", label: "Offres", icon: Briefcase },
          { id: "candidats", label: "Candidats", icon: Users },
          { id: "alertes", label: "Alertes", icon: Bell },
          { id: "coordonnees", label: "Coord.", icon: Building2, dot: !isCompanyComplete },
          { id: "compte", label: "Compte", icon: UserCircle },
        ]}
      />
      <div className="h-16 md:h-0" aria-hidden="true" />
      </DashboardThemeWrapper>
    </Layout>
  );
}
